// native-bridge.ts
export type Resolver<T> = (value: T) => void;

/** Polls for a native shim the shell may inject just after page start; false if it never appears. */
export async function waitForNativeFunctionAsync(name: string, timeoutMs = 1500, intervalMs = 50): Promise<boolean> {
    const exists = () => typeof (globalThis as Record<string, unknown>)[name] === "function";
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        if (exists())
            return true;
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return exists();
}

/** Lets the native app drop its launch screen; no-op in browsers and app builds without appReady. */
export function notifyAppReady(): void {
    if (typeof appReady === "function")
        appReady();
}

/** Hands an app token to the native shell, if we're running inside it. */
export function saveAppUserTokenIfAppView(isAppView: boolean, token?: string | null): void {
    if (!isAppView || !token)
        return;

    //@ts-ignore - injected by the native shell; absent in a normal browser
    if (typeof saveAppUserToken == "function")
        //@ts-ignore
        saveAppUserToken(token);
}

/** Result of a native Google Sign-In attempt, echoed back via setNativeGoogleSignInResult. */
export type NativeGoogleSignInResult = { idToken?: string; cancelled?: boolean; error?: string } | null;

/** Opens the native Google sign-in sheet and awaits the result; null when there's no bridge. */
export async function requestNativeGoogleSignInAsync(
    payload: { nonce: string; serverClientId: string }
): Promise<NativeGoogleSignInResult> {
    if (typeof startNativeGoogleSignIn !== "function")
        return null;

    return awaitNativeOnce<NativeGoogleSignInResult>({
        name: "NativeGoogleSignIn",
        setResolver: (r) => { globalThis.pendingGoogleSignInResolve = r; },
        clearResolver: () => { globalThis.pendingGoogleSignInResolve = null; },
        invokeNative: () => startNativeGoogleSignIn!(JSON.stringify(payload)),
        timeoutValue: null,
        timeoutMs: 300000 // a full Google login (password, 2-step) can run minutes; stays under the 10-min nonce TTL
    });
}

export function awaitNativeOnce<T>(opts: {
    setResolver: (resolver: Resolver<T>) => void;
    clearResolver: () => void;
    invokeNative: () => void;
    timeoutValue: T;
    timeoutMs?: number;

    /** Optional label to identify the call in logs */
    name?: string;

    /** Enable/disable logs */
    debug?: boolean;
}): Promise<T> {
    const timeoutMs = opts.timeoutMs ?? 5000;
    const debug = opts.debug ?? false;
    const name = opts.name ?? "awaitNativeOnce";

    const log = (...args: any[]) => {
        if (!debug) return;
        try {
            console.log(`[${name}]`, ...args);
        } catch {
            // ignore logging errors
        }
    };

    const warn = (...args: any[]) => {
        if (!debug) return;
        try {
            console.warn(`[${name}]`, ...args);
        } catch {
            // ignore logging errors
        }
    };

    return new Promise<T>((resolve) => {
        let done = false;

        const finish = (value: T, reason: "resolver" | "timeout" | "invoke-throw") => {
            if (done) return;
            done = true;

            log(`Finishing via ${reason}.`);

            try {
                opts.clearResolver();
                log("Resolver cleared.");
            } catch (err) {
                warn("Error clearing resolver:", err);
            }

            resolve(value);
        };

        // Install the resolver (native will call this later)
        try {
            opts.setResolver((value: T) => {
                log("Resolver invoked with value:", value);
                finish(value, "resolver");
            });

            log("Resolver installed.");
        } catch (err) {
            warn("Error installing resolver:", err);
            finish(opts.timeoutValue, "invoke-throw");
            return;
        }

        // Kick off native call
        try {
            log("Invoking native call...");
            opts.invokeNative();
            log("Native call invoked successfully.");
        } catch (err) {
            warn("Native invoke threw error:", err);
            finish(opts.timeoutValue, "invoke-throw");
            return;
        }

        // Timeout fallback
        setTimeout(() => {
            warn(`Timed out after ${timeoutMs}ms.`);
            finish(opts.timeoutValue, "timeout");
        }, timeoutMs);
    });
}
